// Players API service for CRUD operations

import {httpsCallable} from "firebase/functions";
import {functions} from "../firebase/config";
import {
  getPlayerProfileFunction,
  updatePlayerProfileFunction,
  getPlayerStatisticsFunction,
  assignPlayerRoleFunction,
  GetPlayerProfileData,
  UpdatePlayerProfileData,
  GetPlayerStatisticsData,
  AssignPlayerRoleData,
  GetPlayerProfileResponse,
  UpdatePlayerProfileResponse,
  GetPlayerStatisticsResponse,
  AssignPlayerRoleResponse,
} from "../firebase/functions";

/**
 * Get detailed player profile information
 */
export const getPlayerProfile = async (
  data: GetPlayerProfileData
): Promise<GetPlayerProfileResponse> => {
  try {
    const result = await getPlayerProfileFunction(data);
    return result.data as GetPlayerProfileResponse;
  } catch (error) {
    // Error getting player profile - rethrow for handling by caller
    throw error;
  }
};

/**
 * Update player profile information
 */
export const updatePlayerProfile = async (
  data: UpdatePlayerProfileData
): Promise<UpdatePlayerProfileResponse> => {
  try {
    const result = await updatePlayerProfileFunction(data);
    return result.data as UpdatePlayerProfileResponse;
  } catch (error) {
    // Error updating player profile - rethrow for handling by caller
    throw error;
  }
};

/**
 * Get comprehensive player statistics
 */
export const getPlayerStatistics = async (
  data: GetPlayerStatisticsData
): Promise<GetPlayerStatisticsResponse> => {
  try {
    const result = await getPlayerStatisticsFunction(data);
    return result.data as GetPlayerStatisticsResponse;
  } catch (error) {
    // Error getting player statistics - rethrow for handling by caller
    throw error;
  }
};

/**
 * Assign or remove roles from a player (Admin only)
 */
export const assignPlayerRole = async (
  data: AssignPlayerRoleData
): Promise<AssignPlayerRoleResponse> => {
  try {
    const result = await assignPlayerRoleFunction(data);
    return result.data as AssignPlayerRoleResponse;
  } catch (error) {
    // Error assigning player role - rethrow for handling by caller
    throw error;
  }
};

/**
 * Check if user has a pending DM approval
 */
export const checkUserDMApproval = async (
  userId: string
): Promise<{hasPendingApproval: boolean}> => {
  try {
    const checkDMApproval = httpsCallable(functions, "checkUserDMApproval");
    const result = await checkDMApproval({userId});
    return result.data as {hasPendingApproval: boolean};
  } catch (error) {
    // Error checking DM approval status - rethrow for handling by caller
    throw error;
  }
};

/**
 * Withdraw DM application
 */
export const withdrawDMApplication = async (): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    const withdrawDMApplication = httpsCallable(
      functions,
      "withdrawDMApplication"
    );
    const result = await withdrawDMApplication();
    return result.data as {success: boolean; message: string};
  } catch (error) {
    // Error withdrawing DM application - rethrow for handling by caller
    throw error;
  }
};

/**
 * Request DM approval (for existing users)
 */
export const requestDMApproval = async (): Promise<{
  success: boolean;
  message: string;
}> => {
  try {
    const requestDMApproval = httpsCallable(functions, "requestDMApproval");
    const result = await requestDMApproval();
    return result.data as {success: boolean; message: string};
  } catch (error) {
    // Error requesting DM approval - rethrow for handling by caller
    throw error;
  }
};
